import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FiMenu, FiX, FiSettings, FiLogOut } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { Button, Dialog, DialogActions } from './ds';
import { isAdmin } from '../utils/roles';
import { useAppNavItems } from '../hooks/useAppNavItems';

const MobileTopNav = () => {
  const { logout, profile, displayName } = useAuth();
  const navigate = useNavigate();
  const navItems = useAppNavItems(profile);
  const admin = isAdmin(profile);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  useEffect(() => {
    if (!showLogoutConfirm) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowLogoutConfirm(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showLogoutConfirm]);

  const closeMenu = () => setMenuOpen(false);

  const handleConfirmLogout = async () => {
    setShowLogoutConfirm(false);
    closeMenu();
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <>
      <header className="mobile-top-nav" role="banner">
        <span className="mobile-top-nav__brand">The Menu Co.</span>
        <button
          type="button"
          className="mobile-top-nav__toggle"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-controls="mobile-nav-sheet"
          aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
        >
          {menuOpen ? <FiX size={22} /> : <FiMenu size={22} />}
        </button>
      </header>

      {menuOpen ? (
        <div
          className="mobile-nav-backdrop"
          role="presentation"
          onClick={closeMenu}
        />
      ) : null}

      <nav
        id="mobile-nav-sheet"
        className={`mobile-nav-sheet${menuOpen ? ' is-open' : ''}`}
        aria-label="Navegación principal"
        aria-hidden={!menuOpen}
      >
        <div className="mobile-nav-sheet__user">
          <p className="mobile-nav-sheet__name">{displayName || 'Usuario'}</p>
          <p className="mobile-nav-sheet__role">{admin ? 'Administrador' : 'Usuario'}</p>
        </div>

        <div className="mobile-nav-sheet__links">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `mobile-nav-sheet__link${isActive ? ' is-active' : ''}`
              }
              onClick={closeMenu}
            >
              <Icon size={20} aria-hidden />
              <span>{label}</span>
            </NavLink>
          ))}
        </div>

        <div className="mobile-nav-sheet__footer">
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `mobile-nav-sheet__action mobile-nav-sheet__action--settings${isActive ? ' is-active' : ''}`
            }
            onClick={closeMenu}
          >
            <FiSettings size={18} aria-hidden />
            <span>Configuración</span>
          </NavLink>
          <button
            type="button"
            className="mobile-nav-sheet__action mobile-nav-sheet__action--logout"
            onClick={() => {
              closeMenu();
              setShowLogoutConfirm(true);
            }}
          >
            <FiLogOut size={18} aria-hidden />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </nav>

      {showLogoutConfirm ? (
        <Dialog
          open
          title="Cerrar sesión"
          onClose={() => setShowLogoutConfirm(false)}
          footer={
            <DialogActions>
              <Button variant="outline" onClick={() => setShowLogoutConfirm(false)}>
                Cancelar
              </Button>
              <Button variant="solid" onClick={handleConfirmLogout}>
                Cerrar sesión
              </Button>
            </DialogActions>
          }
        >
          <p className="text-secondary">¿Está seguro de que desea cerrar sesión?</p>
        </Dialog>
      ) : null}
    </>
  );
};

export default MobileTopNav;
